import { SignupState } from "../../../store/signup/SignupState"
import { NEW_COMPANY_CODE } from "../../ntuc/userService/company"
import { BusinessNatureValidator } from "./BusinessNatureValidator"
import { CompanyNameValidator } from "./CompanyNameValidator"
import { FloorNumberValidator } from "./FloorNumberValidation"
import { HouseNumberValidator } from "./HouseNumberValidator"
import { JobTitleError, JobTitleValidator } from "./JobTitleValidator"
import { MonthlySalaryValidator } from "./MonthlySalaryField"
import { OccupationalGroupValidator } from "./OccupationalGroupField"
import { PostalCodeValidator } from "./PostalCodeValidator"
import { StreetNameValidator } from "./StreetNameValidator"
import { UnitNumberValidator } from "./UnitNumberValdation"
import { disableFieldCheck } from "./disableFieldCheck"
import { EligibilityParams, getMembershipEligibilityData } from "../../ntuc/memberService/membershipEligibility"
import { ErrorType } from "../../ntuc/applicationError"
import { InputDataExclusion } from "../../ntuc/userService/occupationGroup"
import { OtherCompanyNameValidator } from "./OtherCompanyNameValidator"

export default (state: SignupState): Promise<void> => new Promise((resolve, reject) => {
    const validPostalCode = validatePostalCodeText(state.addressAndEmploymentDetail.postalCode)
    const validateHouseNumber = validateHouseNumberText(state.addressAndEmploymentDetail.houseNumber)
    const validateStreetName = validateStreetNameText(state.addressAndEmploymentDetail.streetName)
    const validateFloorNumber = validateFloorNumberText(state.addressAndEmploymentDetail.floorNumber)
    const validateUnitNumber = validateUnitNumberText(state.addressAndEmploymentDetail.unitNumber)
    const validateJobTitle = validateJobTitleText(state.addressAndEmploymentDetail.jobTitle)
    const validateCompanyName = state.addressAndEmploymentDetail.companyName.code == NEW_COMPANY_CODE ? validateOtherCompanyNameText(state.addressAndEmploymentDetail.companyName.name) : validateCompanyNameText(state.addressAndEmploymentDetail.companyName.name)
    const validateOccupationalGroup = validateOccupationalGroupText(state.addressAndEmploymentDetail.occupationalGroup.value)
    const validateMonthlySalary = validateMonthlySalaryText(state.addressAndEmploymentDetail.monthlySalary.value)
    const validateBusinessNature = validateBusinessNatureText(state.addressAndEmploymentDetail.companyName.businessNature ?? ``)
    const isMyInfoAndSCPR = state.disabledFields.myInfo && state.myInfo.isSCPR
    const dataInputExclusion = state.addressAndEmploymentDetail.occupationalGroup.inputDataExclusion ? state.addressAndEmploymentDetail.occupationalGroup.inputDataExclusion : `[]`
    const parsedInputExclusion : string[] = JSON.parse(dataInputExclusion)

    const validationResult = initialAddressAndEmployementFormValidation()
    let isValid = true
    let shouldCheckJobTitle = false

    if (validPostalCode) {
        validationResult.postalCodeValidationMessage = validPostalCode
        isValid = false
    }
    if (validateHouseNumber) {
        validationResult.houseNumberValidationMessage = validateHouseNumber
        isValid = false
    }
    if (validateStreetName) {
        validationResult.streetNameValidationMessage = validateStreetName
        isValid = false
    }
    if (validateUnitNumber && !isMyInfoAndSCPR) { // for myinfo sign up, skip validation for unit when user is SCPR
        validationResult.unitNumberValidationMessage = validateUnitNumber
        isValid = false
    }
    if (validateFloorNumber && !isMyInfoAndSCPR) { // for myinfo sign up, skip validation for floor when user is SCPR
        validationResult.floorNumberValidationMessage = validateFloorNumber
        isValid = false
    }
    if (validateOccupationalGroup) {
        validationResult.occupationalGroupValidationMessage = validateOccupationalGroup
        isValid = false
    }

    if (!disableFieldCheck.has(state.addressAndEmploymentDetail.occupationalGroup.code)) {
        if (!parsedInputExclusion.includes(InputDataExclusion.JobTitle)) {
            if (validateJobTitle) {
                validationResult.jobTitleValidationMessage = validateJobTitle
                isValid = false
            } else {
                shouldCheckJobTitle = true
            }
        }

        if (!parsedInputExclusion.includes(InputDataExclusion.Company)) {
            if (validateCompanyName) {
                validationResult.companyNameValidationMessage = validateCompanyName
                isValid = false
            }
        }

        if (!parsedInputExclusion.includes(InputDataExclusion.MonthlyGrossSalary)) {
            if (validateMonthlySalary) {
                validationResult.monthlySalaryValidationMessage = validateMonthlySalary
                isValid = false
            }
        }

        if (!parsedInputExclusion.includes(InputDataExclusion.Company)) {
            if (validateBusinessNature
                && (state.addressAndEmploymentDetail.companyName.code === NEW_COMPANY_CODE || state.addressAndEmploymentDetail.companyName.code === ``)
            ) {
                validationResult.businessNatureValidationMessage = validateBusinessNature
                isValid = false
            }
        }
    }

    if (shouldCheckJobTitle) {
        runJobTitleBackendCheck(state, resolve, reject, isValid, validationResult)
    } else {
        validationPromiseResult(resolve, reject, isValid, validationResult)
    }
})

const runJobTitleBackendCheck = (state: SignupState, resolve: () => void, reject: (reason?: any) => void, isValid: boolean, validationResult: AddressAndEmployementFormValidation) => {
    checkJobTitleEligibility(state)
        .then((jobTitleValidationMessage) => {
            validationResult.jobTitleValidationMessage = jobTitleValidationMessage ?? ``
            if (validationResult.jobTitleValidationMessage) {
                isValid = false
            }
        })
        .catch(() => {
            validationResult.jobTitleValidationMessage = JobTitleError.BLANK
            isValid = false
        })
        .finally(() => {
            validationPromiseResult(resolve, reject, isValid, validationResult)
        })
}

const validationPromiseResult = (resolve: () => void, reject: (reason?: any) => void, isValid: boolean, validationResult: AddressAndEmployementFormValidation) => {
    if (isValid) {
        resolve()
    } else {
        reject(validationResult)
    }
}

export const validatePostalCodeText = (value: string): string | undefined => {
    return (new PostalCodeValidator()).validate(value)
}

export const validateHouseNumberText = (value: string): string | undefined => {
    return (new HouseNumberValidator()).validate(value)
}

export const validateStreetNameText = (value: string): string | undefined => {
    return (new StreetNameValidator()).validate(value)
}

export const validateFloorNumberText = (value: string): string | undefined => {
    return (new FloorNumberValidator()).validate(value)
}

export const validateUnitNumberText = (value: string): string | undefined => {
    return (new UnitNumberValidator()).validate(value)
}

export const validateJobTitleText = (value: string): string | undefined => {
    return (new JobTitleValidator()).validate(value)
}

export const validateCompanyNameText = (value: string): string | undefined => {
    return (new CompanyNameValidator()).validate(value)
}
export const validateOtherCompanyNameText = (value: string): string | undefined => {
    return (new OtherCompanyNameValidator()).validate(value)
}
export const validateOccupationalGroupText = (value: string): string | undefined => {
    return (new OccupationalGroupValidator()).validate(value)
}
export const validateMonthlySalaryText = (value: string): string | undefined => {
    return (new MonthlySalaryValidator()).validate(value)
}
export const validateBusinessNatureText = (value: string): string | undefined => {
    return (new BusinessNatureValidator()).validate(value)
}

export const checkJobTitleEligibility = async (state: SignupState): Promise<string | undefined> => {
    const eligibilityParams: EligibilityParams = {
        jobTitle: state.addressAndEmploymentDetail.jobTitle,
        occupationGroupCode: ``,
        companyCode: ``,
    }
    try {
        await getMembershipEligibilityData(state.personalDetail.nric, eligibilityParams)
    } catch (err: any) {
        if (err.error === ErrorType.JobTitleInEligible) {
            return err.errorDescription
        }
    }
}

export interface AddressAndEmployementFormValidation {
    postalCodeValidationMessage: string | undefined
    houseNumberValidationMessage: string | undefined
    streetNameValidationMessage: string | undefined
    floorNumberValidationMessage: string | undefined
    unitNumberValidationMessage: string | undefined
    jobTitleValidationMessage: string | undefined
    companyNameValidationMessage: string | undefined
    occupationalGroupValidationMessage: string | undefined
    monthlySalaryValidationMessage: string | undefined
    businessNatureValidationMessage: string | undefined
}

const initialAddressAndEmployementFormValidation = (): AddressAndEmployementFormValidation => ({
    postalCodeValidationMessage: undefined,
    houseNumberValidationMessage: undefined,
    streetNameValidationMessage: undefined,
    floorNumberValidationMessage: undefined,
    unitNumberValidationMessage: undefined,
    jobTitleValidationMessage: undefined,
    companyNameValidationMessage: undefined,
    occupationalGroupValidationMessage: undefined,
    monthlySalaryValidationMessage: undefined,
    businessNatureValidationMessage: undefined,
})
